import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, of } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';
import { TReducerState } from '../../app.config';
import { IDashboardWithWidgets } from '../../pages/dashboard-content/dashboard-content.interface';
import { HttpUtilitiesService, IBaseResponse } from '../../utilities/http-utilities.service';
import { dashboardContentActions } from './dashboard-content.actions';
import { DashboardContentService } from './dashboard-content.service';

@Injectable()
export class DashboardContentEffects {
  private readonly store: Store<TReducerState> = inject(Store<TReducerState>);
  private readonly dashboardId = this.store.selectSignal(({ dashboardContent }) => dashboardContent.dashboardData?.id);

  fetchDashboardContents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dashboardContentActions.fetchDashboardContents.type),
      switchMap(({ dashboardId }: ReturnType<typeof dashboardContentActions.fetchDashboardContents>) => {
        return this.service.getDashboardContent(dashboardId, { params: this.service.dashboardContentLoadParams }).pipe(
          switchMap((response: IBaseResponse<IDashboardWithWidgets>) => {
            return of(
              dashboardContentActions.fetchDashboardContentsCompleted({
                dashboardWithWidgets: { ...response.data },
              }),
            );
          }),
          catchError(() => of(dashboardContentActions.fetchError({ exceptionSource: 'fetchDashboardContents' }))),
        );
      }),
    ),
  );

  reassignWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dashboardContentActions.reassignWidgets.type),
      switchMap(({ dashboardId, payload }: ReturnType<typeof dashboardContentActions.reassignWidgets>) => {
        return this.service.createAttachmentThenReassign(dashboardId, payload).pipe(
          switchMap(() => {
            return of(dashboardContentActions.reassignWidgetsCompleted());
          }),
          catchError(() => of(dashboardContentActions.fetchError({ exceptionSource: 'reassignWidgets' }))),
        );
      }),
    ),
  );

  updateNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dashboardContentActions.updateNote.type),
      mergeMap(({ widgetId, note }: ReturnType<typeof dashboardContentActions.updateNote>) => {
        return this.service.updateNote(widgetId, note).pipe(
          switchMap(() => EMPTY),
          // Resync from the server so the optimistic in-grid change is reverted on failure.
          catchError(() => of(dashboardContentActions.fetchDashboardContents({ dashboardId: this.dashboardId()! }))),
        );
      }),
    ),
  );

  updateDefaultFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dashboardContentActions.updateDefaultFilters.type),
      switchMap(({ defaultFilterCard }: ReturnType<typeof dashboardContentActions.updateDefaultFilters>) => {
        return this.service.update({ dto: { defaultFilterCard }, id: this.dashboardId()! }).pipe(
          switchMap(() => EMPTY),
          catchError(() => of(dashboardContentActions.fetchError({ exceptionSource: 'reassignWidgets' }))),
        );
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly service: DashboardContentService,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {}
}
